import React, { useEffect, useMemo } from 'react';

import clsx from 'clsx';
import i18n from 'i18n-react';

import { getListingAmenities } from 'helpers/getListingAmenities';

import { useDirectoryConfig } from 'hooks/useDirectoryConfig/useDirectoryConfig';

import { ProfileAbout } from './components/ProfileAbout/ProfileAbout';
import { ProfileAddress } from './components/ProfileAddress/ProfileAddress';
import { ProfileAmenities } from './components/ProfileAmenities/ProfileAmenities';
import { ProfileContact } from './components/ProfileContact/ProfileContact';
import { ProfileDescription } from './components/ProfileDescription/ProfileDescription';
import { ProfileGallery } from './components/ProfileGallery/ProfileGallery';
import { ProfileHeader } from './components/ProfileHeader/ProfileHeader';
import { ProfileMap } from './components/ProfileMap/ProfileMap';
import { ProfileRooms } from './components/ProfileRooms/ProfileRooms';
import { ProfileSocials } from './components/ProfileSocials/ProfileSocials';
import { useDirectoryProfileConfig } from './hooks/useDirectoryProfileConfig/useDirectoryProfileConfig';
import { useListingDetail } from './hooks/useListingDetail/useListingDetail';
import type { ListingKind } from './types';

import './_directoryProfileView.scss';

const ENABLE_LOCAL_CONFIG_JSON = false; // Set to true to load config from config.json instead of API. FOR TESTING ONLY!!!

interface MatchParams {
  id: string;
}

export interface DirectoryProfileViewProps {
  match: { params: MatchParams };
}

export function DirectoryProfileView({ match }: DirectoryProfileViewProps) {
  const { id } = match.params;
  const searchParams = useMemo(() => {
    return new URLSearchParams(window.location.search);
  }, []);

  const kind: ListingKind = searchParams.get('type') === 'premium' ? 'premium' : 'free';
  const appId = searchParams.get('appId') ?? '';
  const font = searchParams.get('font') ?? 'Source Sans Pro';
  const zoom = Number(searchParams.get('zoom') ?? '12');
  const table = searchParams.get('table') ?? '';

  const { data: listing, isLoading } = useListingDetail(id, kind, appId, table);
  const directoryConfigState = useDirectoryConfig(ENABLE_LOCAL_CONFIG_JSON);
  const profileConfig = useDirectoryProfileConfig(appId);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = `https://fonts.googleapis.com/css?family=${encodeURIComponent(font)}:400,400i,700,700i`;
    document.head.appendChild(link);
    return () => {
      document.head.removeChild(link);
    };
  }, [font]);

  if (isLoading || !profileConfig) return null;

  if (!listing) {
    return (
      <div className={clsx('directory-profile-view')}>
        {i18n.translate('directoryProfileView.listingNotFound') as string}
      </div>
    );
  }

  const googleMapsApiKey =
    directoryConfigState.status === 'found' ? directoryConfigState.config.map.googleMapsApiKey : '';
  const amenities =
    listing.kind === 'free'
      ? getListingAmenities(listing).filter(({ column }) => {
          return profileConfig.activeAmenities[column];
        })
      : [];

  return (
    <div className={clsx('directory-profile-view')} style={{ '--profile-font': font } as React.CSSProperties}>
      {listing.kind === 'premium' ? (
        <div className="profile-content">
          <div className="profile-header">
            {listing.headerImages[0] && <img src={listing.headerImages[0]} alt={listing.propertyName} />}
            <h1 className="profile-header__name">{listing.propertyName}</h1>
            {listing.description && <div dangerouslySetInnerHTML={{ __html: listing.description }} />}
            {listing.address && <p>{listing.address}</p>}
            {listing.phoneNumber && <p>{listing.phoneNumber}</p>}
          </div>
        </div>
      ) : (
        <React.Fragment>
          {profileConfig.showMap && googleMapsApiKey && (
            <ProfileMap
              latitude={listing.latitude}
              longitude={listing.longitude}
              city={listing.city}
              zoom={zoom}
              googleMapsApiKey={googleMapsApiKey}
            />
          )}
          <div className="profile-content">
            <ProfileHeader
              company={listing.company}
              officialName={profileConfig.showOfficialName ? listing.official_name : null}
              subCatname={profileConfig.showSubcategory ? listing.sub_catname : null}
              addr1={profileConfig.showAddressInHeader ? listing.addr1 : null}
              city={profileConfig.showCityInHeader ? listing.city : null}
            />
            <hr className="profile-content__divider" />

            {profileConfig.showGallery && listing.images.length > 0 ? (
              <React.Fragment>
                <div className="listing-detail-layout has-gallery">
                  <div className="listing-detail-gallery-col">
                    <ProfileGallery images={listing.images} propertyName={listing.company} />
                  </div>
                  <div className="listing-detail-text-col">
                    <ProfileDescription
                      description={profileConfig.showDescription ? listing.description : null}
                      description2={profileConfig.showDescription2 ? listing.description_2 : null}
                      description3={profileConfig.showDescription3 ? listing.description_3 : null}
                    />
                  </div>
                </div>
                <hr className="profile-content__divider" />
              </React.Fragment>
            ) : (
              <ProfileDescription
                description={profileConfig.showDescription ? listing.description : null}
                description2={profileConfig.showDescription2 ? listing.description_2 : null}
                description3={profileConfig.showDescription3 ? listing.description_3 : null}
              />
            )}

            <div className="listing-detail-meta">
              <ProfileContact
                webUrl={profileConfig.showWebsite ? listing.web_url : null}
                phone={profileConfig.showPhone ? listing.phone : null}
                altPhone={profileConfig.showAltPhone ? listing.alt_phone : null}
                tollFree={profileConfig.showTollFree ? listing.toll_free : null}
                fax={profileConfig.showFax ? listing.fax : null}
                primaryContactFullName={profileConfig.showContactPerson ? listing.primary_contact_full_name : null}
                primaryContactTitle={profileConfig.showContactPerson ? listing.primary_contact_title : null}
                email={profileConfig.showEmail ? listing.email : null}
              />
              {profileConfig.showAmenitiesInAmenities && (
                <ProfileAmenities
                  typeName={listing.type_name}
                  numberOfRooms={profileConfig.showRoomsInAccommodationType ? listing.number_of_rooms : null}
                  amenities={amenities}
                />
              )}
              <div className="listing-detail-meta__row">
                <ProfileAddress
                  addr1={profileConfig.showAddr1InAddress ? listing.addr1 : null}
                  addr2={profileConfig.showAddr2InAddress ? listing.addr2 : null}
                  addr3={profileConfig.showAddr3InAddress ? listing.addr3 : null}
                  city={profileConfig.showCityInAddress ? listing.city : null}
                  region={profileConfig.showRegionInAddress ? listing.region : null}
                  state={profileConfig.showStateInAddress ? listing.state : null}
                  country={profileConfig.showCountryInAddress ? listing.country : null}
                  zip={profileConfig.showZipInAddress ? listing.zip : null}
                />
                {profileConfig.showRoomsInNumberOfRooms && <ProfileRooms numberOfRooms={listing.number_of_rooms} />}
                <ProfileAbout
                  category={profileConfig.showCategory ? listing.category : null}
                  catName={profileConfig.showCatName ? listing.cat_name : null}
                  starRating={profileConfig.showStarRating ? listing.rank_name : null}
                  cuisine={profileConfig.showCuisine ? listing.cuisine : null}
                  amenities={profileConfig.showAmenitiesInAbout ? amenities : []}
                />
              </div>
              <ProfileSocials
                tripadvisor={profileConfig.showTripadvisor ? listing.tripadvisor : null}
                yelp={profileConfig.showYelp ? listing.yelp : null}
                youtube={profileConfig.showYoutube ? listing.youtube : null}
                instagram={profileConfig.showInstagram ? listing.instagram : null}
                facebook={profileConfig.showFacebook ? listing.facebook : null}
                twitter={profileConfig.showTwitter ? listing.twitter : null}
                pinterest={profileConfig.showPinterest ? listing.pinterest : null}
                socialMedia={profileConfig.showSocialMedia ? listing.social_media : null}
              />
            </div>
          </div>
        </React.Fragment>
      )}
    </div>
  );
}
